import { useEffect, useRef } from 'react'
import { aiApi } from '@/api/ai'
import { watchJobStream } from '@/api/jobStream'
import type { AiExplanationResult, AiPracticeResult, AiJobStatusResponse } from '@/types/ai'

export interface UseAiJobPollerOptions {
  /** jobId to poll. Pass null to disable. */
  jobId: string | null
  onDone: (response: AiJobStatusResponse) => void
  onError: (error: string) => void
  /** Called on each poll with the current job status (PENDING, PROCESSING, etc.). */
  onStatus?: (status: string) => void
  /** Polling interval in ms. Default: 1000 */
  intervalMs?: number
  /** Stop polling after this many attempts. Default: 90 (~90s at 1s interval). */
  maxAttempts?: number
}

/**
 * Watches AI job completion via SSE when possible, with polling fallback.
 */
export function useAiJobPoller({
  jobId,
  onDone,
  onError,
  onStatus,
  intervalMs = 1000,
  maxAttempts = 90,
}: UseAiJobPollerOptions) {
  const onDoneRef = useRef(onDone)
  const onErrorRef = useRef(onError)
  const onStatusRef = useRef(onStatus)

  useEffect(() => {
    onDoneRef.current = onDone
    onErrorRef.current = onError
    onStatusRef.current = onStatus
  })

  useEffect(() => {
    if (!jobId) return

    let cancelled = false
    let intervalId: ReturnType<typeof setInterval> | null = null
    const abortController = new AbortController()

    const handleStatus = (res: AiJobStatusResponse) => {
      const normalized = res.status?.toUpperCase()
      if (normalized) {
        onStatusRef.current?.(normalized)
      }
      if (normalized === 'DONE') {
        onDoneRef.current(res)
      } else if (normalized === 'FAILED') {
        onErrorRef.current(res.error ?? 'AI generation failed. Please try again.')
      }
    }

    const startPolling = () => {
      let attempts = 0

      const poll = async () => {
        if (cancelled) return
        attempts += 1

        if (attempts > maxAttempts) {
          if (intervalId) clearInterval(intervalId)
          onErrorRef.current('AI generation timed out. Please try again.')
          return
        }

        try {
          const res = await aiApi.getJobStatus(jobId)
          if (cancelled) return
          handleStatus(res.data)
          const normalized = res.data.status?.toUpperCase()
          if (normalized === 'DONE' || normalized === 'FAILED') {
            if (intervalId) clearInterval(intervalId)
          }
        } catch {
          if (cancelled) return
          if (intervalId) clearInterval(intervalId)
          onErrorRef.current('Failed to check job status. Please try again.')
        }
      }

      void poll()
      intervalId = setInterval(poll, intervalMs)
    }

    void (async () => {
      try {
        const streamed = await watchJobStream(jobId, abortController.signal, (status) => {
          if (cancelled) return
          handleStatus(status)
        })
        if (!streamed && !cancelled) {
          startPolling()
        }
      } catch {
        if (!cancelled) {
          startPolling()
        }
      }
    })()

    return () => {
      cancelled = true
      abortController.abort()
      if (intervalId) clearInterval(intervalId)
    }
  }, [jobId, intervalMs, maxAttempts])
}

// Type guards for discriminating result types
export function isExplanationResult(result: unknown): result is AiExplanationResult {
  return typeof result === 'object' && result !== null && 'explanation' in result
}

export function isPracticeResult(result: unknown): result is AiPracticeResult {
  return typeof result === 'object' && result !== null && 'content' in result
}

export interface PersonalizedArticleJobResult {
  jobType: 'GENERATE_ARTICLE'
  articleId: string
  title: string
  model?: string
  tokensUsed?: number
}

export function isPersonalizedArticleResult(result: unknown): result is PersonalizedArticleJobResult {
  return typeof result === 'object' && result !== null && 'articleId' in result
}
